import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import ProductCard from "@/components/ProductCard";
import Marquee from "@/components/Marquee";
import { ArrowRight, Sparkles, Download, ShieldCheck, RefreshCcw, Star } from "lucide-react";
import { BRANDING, CATEGORY_DECK } from "@/branding";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [newReleases, setNewReleases] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true, limit: 8 } })
      .then((r) => setFeatured(r.data || []))
      .catch(() => {});
    api.get("/products", { params: { limit: 8 } })
      .then((r) => setNewReleases(r.data || []))
      .catch(() => {});
    api.get("/products/bestsellers", { params: { limit: 8 } })
      .then((r) => setBestSellers(r.data || []))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-cream text-plum-deep" data-testid="home-page">
      <Header />
      <CartDrawer />

      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full blur-3xl opacity-30" style={{ background: "radial-gradient(closest-side, var(--brand-gold-pale), transparent)" }} />
          <div className="absolute -bottom-24 -right-20 w-80 h-80 rounded-full blur-3xl opacity-25" style={{ background: "radial-gradient(closest-side, var(--brand-plum), transparent)" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 pt-12 lg:pt-20 pb-12 lg:pb-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold">
              <Sparkles className="h-3.5 w-3.5 twinkle" /> {BRANDING.heroEyebrow}
            </div>
            <h1 className="editorial font-serif-display mt-5 text-4xl sm:text-5xl lg:text-7xl leading-[1.02] text-plum-deep">
              <span className="block">The <em className="italic text-plum">digital boutique</em></span>
              <span className="block mt-2">for a life well-lived.</span>
            </h1>
            <p className="mt-6 text-plum/80 max-w-xl text-base sm:text-lg leading-relaxed">
              {BRANDING.tagline}
            </p>
            <div className="mt-9 flex flex-col sm:flex-row sm:items-center gap-3">
              <Link to="/shop" className="btn-primary" data-testid="hero-shop-btn">
                {BRANDING.heroCtaPrimary}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="#categories" className="btn-ghost-gold" data-testid="hero-categories-btn">
                {BRANDING.heroCtaSecondary}
              </a>
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs text-plum/70">
              <span className="inline-flex items-center gap-1">
                <Star className="h-3.5 w-3.5 text-gold fill-current" /> Original digital products
              </span>
              <span className="hidden sm:inline-flex items-center gap-1">
                <Download className="h-3.5 w-3.5 text-gold" /> Instant delivery
              </span>
            </div>
          </div>
          <div className="lg:col-span-5 fade-up" style={{ animationDelay: "180ms" }}>
            <div className="relative aspect-[4/5] rounded-sm overflow-hidden border border-brand bg-cream-2">
              <img src={BRANDING.heroImageUrl} alt="A thoughtfully arranged desk with digital tools" className="h-full w-full object-cover" />
              <div className="absolute top-4 left-4 bg-cream/90 backdrop-blur-sm border border-gold/40 rounded-full px-3 py-1 text-[11px] uppercase tracking-widest text-plum">
                <Sparkles className="inline h-3 w-3 text-gold mr-1" /> New
              </div>
            </div>
            <div className="mt-3 text-[11px] uppercase tracking-widest text-plum/60 text-right">New drops every week</div>
          </div>
        </div>
      </section>

      <Marquee items={["Business & Money", "Templates & Printables", "DIY & How-To", "Wellness & Lifestyle", "Spiritual & Tarot", "Home & Organization", "Travel", "Creator Resources"]} />

      {/* ================= TRUST BAND ================= */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-14 lg:py-16">
        <div className="grid md:grid-cols-3 gap-4 md:gap-6">
          {BRANDING.trust.map((t, i) => (
            <div key={t.title} className="trust-badge fade-up" style={{ animationDelay: `${i * 80}ms` }} data-testid={`trust-${i}`}>
              <div className="flex items-start gap-4">
                <div className="icon-wrap shrink-0">
                  {i === 0 ? <Download className="h-5 w-5" /> : i === 1 ? <ShieldCheck className="h-5 w-5" /> : <RefreshCcw className="h-5 w-5" />}
                </div>
                <div>
                  <div className="font-serif-display text-xl text-plum-deep">{t.title}</div>
                  <div className="text-sm text-plum/70 mt-1 leading-relaxed">{t.body}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ================= CATEGORIES ================= */}
      <section id="categories" className="max-w-7xl mx-auto px-5 lg:px-10 py-10 lg:py-14 scroll-mt-20" data-testid="categories-section">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-gold">Shop by category</div>
            <h2 className="editorial font-serif-display text-3xl sm:text-4xl mt-3 text-plum-deep">Find your next favorite tool</h2>
          </div>
          <Link to="/shop" className="btn-outline-plum" data-testid="all-categories-link">
            All products <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          {CATEGORY_DECK.map((c, idx) => (
            <Link
              key={c.slug}
              to={`/shop?category=${c.slug}`}
              className="cat-tile fade-up p-5 sm:p-6 min-h-[150px] flex flex-col justify-between"
              style={{ animationDelay: `${idx * 50}ms` }}
              data-testid={`cat-tile-${c.slug}`}
            >
              <div className="text-[10px] uppercase tracking-[0.25em] text-gold">0{idx + 1}</div>
              <div>
                <div className="font-serif-display text-xl sm:text-2xl text-plum-deep leading-tight">{c.name}</div>
                <div className="text-xs text-plum/60 mt-2 leading-relaxed">{c.blurb}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ================= FEATURED ================= */}
      <ProductSection
        eyebrow="Hand-picked"
        title="Featured"
        subtitle="Our current favorites — the ones we'd recommend to a friend."
        linkTo="/shop?featured=1"
        products={featured}
        emptyLabel="Mark products as featured in the admin to curate this row."
        testid="featured-section"
      />

      <div className="max-w-7xl mx-auto px-5 lg:px-10"><div className="gold-rule" /></div>

      {/* ================= NEW RELEASES ================= */}
      <ProductSection
        eyebrow="Fresh out of the studio"
        title="New Releases"
        subtitle="The latest additions to the shop."
        linkTo="/shop?new=1"
        products={newReleases}
        emptyLabel="Create products in the admin to populate this row."
        testid="new-releases-section"
      />

      <div className="max-w-7xl mx-auto px-5 lg:px-10"><div className="gold-rule" /></div>

      {/* ================= BEST SELLERS ================= */}
      <ProductSection
        eyebrow="Loved by customers"
        title="Best Sellers"
        subtitle="Top picks based on real orders."
        linkTo="/shop?bestseller=1"
        products={bestSellers}
        emptyLabel="Once orders start rolling in, your top sellers appear here."
        testid="best-sellers-section"
      />

      {/* ================= EDITORIAL CTA ================= */}
      <section className="relative mt-16 bg-plum-deep text-cream overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-20"
             style={{ background: "radial-gradient(circle at 20% 0%, var(--brand-gold) 0%, transparent 40%)" }} />
        <div className="relative max-w-5xl mx-auto px-5 lg:px-10 py-16 lg:py-24 text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] text-gold">A little magic in your inbox</div>
          <h2 className="editorial font-serif-display text-3xl sm:text-5xl mt-5 leading-tight">
            Thoughtful tools. Delivered the moment you need them.
          </h2>
          <p className="mt-5 text-cream/70 max-w-xl mx-auto text-base">
            From planners to tarot workbooks — browse the full catalog and download instantly after checkout.
          </p>
          <div className="mt-9 inline-flex gap-3 flex-wrap justify-center">
            <Link to="/shop" className="btn-primary bg-gold text-plum-deep hover:bg-gold-soft" style={{ background: "var(--brand-gold)", color: "var(--brand-plum-deep)" }} data-testid="cta-shop-btn">
              Shop the full catalog <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/about" className="btn-ghost-gold text-cream" style={{ borderColor: "var(--brand-gold)", color: "var(--brand-cream)" }} data-testid="cta-about-btn">
              About the shop
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function ProductSection({ eyebrow, title, subtitle, linkTo, products, emptyLabel, testid }) {
  return (
    <section className="max-w-7xl mx-auto px-5 lg:px-10 py-14 lg:py-20" data-testid={testid}>
      <div className="flex items-end justify-between gap-4 flex-wrap mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-gold">{eyebrow}</div>
          <h2 className="editorial font-serif-display text-3xl sm:text-4xl mt-3 text-plum-deep">{title}</h2>
          {subtitle && <p className="text-sm text-plum/70 mt-2 max-w-xl">{subtitle}</p>}
        </div>
        <Link to={linkTo} className="text-sm underline underline-offset-4 text-plum-deep hover:no-underline">See all</Link>
      </div>
      {products.length === 0 ? (
        <div className="border border-dashed border-brand rounded-sm py-16 text-center text-plum/60 bg-white/60 text-sm" data-testid={`${testid}-empty`}>
          {emptyLabel}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 sm:gap-x-6 gap-y-12">
          {products.map((p, i) => (<ProductCard key={p.id} product={p} index={i} />))}
        </div>
      )}
    </section>
  );
}
